import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { addressLine } from '../../lib/format.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { AddressField, addressDraftFrom, addressIsUsable, type AddressDraft } from '../AddressField.js'
import { MapCanvas } from '../map/MapCanvas.js'
import type { MapPin } from '../map/surface.js'
import { SitePicker } from '../SitePicker.js'
import { Button, Field, Input } from '../ui.js'
import { Label, PanelHeading } from './panes.js'
import type { Job } from './types.js'

type Site = NonNullable<Job['site']>

/**
 * The site address, correctable in place — and swappable for a different one.
 *
 * Two distinct actions, kept apart because they mean opposite things.
 * **Edit address** rewrites this property's address, which changes it for
 * every job ever done here; that is right for a misspelt street and wrong for
 * "this one is actually at the other branch". **Change** re-points
 * the job and leaves the site alone. Collapsing them into one editor is how a
 * typo fix silently moves nine years of history to another house.
 *
 * Access notes live on `SiteKnowledgePane` (JOB_EDIT). The street itself is
 * `clients.updateSite`, which needs CLIENT_EDIT — technicians correct how to
 * get in, not the cadastral address.
 */
export function SiteAddressPane({ job, site }: { job: Job; site: Site }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState(false)

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.clients.pathFilter())
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
  }

  const save = useMutation(
    trpc.clients.updateSite.mutationOptions({
      onSuccess: () => {
        setEditing(false)
        invalidate()
      },
    }),
  )

  const move = useMutation(
    trpc.jobs.update.mutationOptions({
      onSuccess: () => {
        setMoving(false)
        invalidate()
      },
    }),
  )

  if (editing) {
    return (
      <SiteAddressForm
        site={site}
        isSaving={save.isPending}
        error={save.error ? errorMessage(save.error) : null}
        onCancel={() => setEditing(false)}
        onSave={(changes) => save.mutate({ id: site.id, ...changes })}
      />
    )
  }

  if (moving) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <Label>Move this job</Label>
          <Button size="sm" type="button" className="ml-auto" onClick={() => setMoving(false)}>
            Cancel
          </Button>
        </div>
        <p className="text-xs text-ink-500">
          Only this job moves. {site.name ?? addressLine(site.address)} keeps its own history.
        </p>
        <SitePicker
          id="job-move-site"
          clientId={job.client.id}
          value={site.id}
          allowNone={false}
          onChange={(siteId) => {
            if (!siteId || siteId === site.id) return
            move.mutate({ id: job.id, siteId })
          }}
        />
        {move.error ? (
          <p className="text-xs text-red-600" role="alert">
            {errorMessage(move.error)}
          </p>
        ) : null}
      </div>
    )
  }

  const lines = addressLines(site.address)

  return (
    <div className="flex flex-col gap-3">
      <PanelHeading
        title="Site"
        action={
          can('JOB_EDIT') ? (
            <button
              type="button"
              title="Move this job to a different property"
              onClick={() => setMoving(true)}
              className="text-xs font-semibold text-brand-600 hover:text-brand-700"
            >
              Change
            </button>
          ) : null
        }
      />

      <SiteMap address={site.address} />

      <div className="flex flex-col gap-0.5">
        {site.name ? (
          <p className="text-[14px] leading-snug font-semibold text-ink-900">{site.name}</p>
        ) : null}
        {lines.length > 0 ? (
          <p
            className={cn(
              'leading-snug',
              site.name ? 'text-[13px] text-ink-600' : 'text-[14px] font-semibold text-ink-900',
            )}
          >
            {lines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </p>
        ) : site.name ? null : (
          <p className="text-[13px] text-ink-500">No address on file</p>
        )}
      </div>

      {site.address || can('CLIENT_EDIT') ? (
        <div className="flex items-center gap-4 text-xs font-semibold">
          {site.address ? (
            <a
              href={mapsUrl(site.address)}
              target="_blank"
              rel="noreferrer"
              className="text-brand-600 hover:text-brand-700"
            >
              Directions
            </a>
          ) : null}
          {can('CLIENT_EDIT') ? (
            <button
              type="button"
              title="Correct this property's address, for every job here"
              onClick={() => setEditing(true)}
              className="text-brand-600 hover:text-brand-700"
            >
              Edit address
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The property on a small map, only when it has coordinates. An address the
 * lookup never placed draws nothing rather than a map of Adelaide.
 */
function SiteMap({ address }: { address: Site['address'] }) {
  const latitude = address?.latitude ?? null
  const longitude = address?.longitude ?? null

  const pins = useMemo<MapPin[] | null>(() => {
    if (latitude === null || longitude === null) return null
    const dot = document.createElement('span')
    dot.style.cssText = [
      'display:block',
      'width:14px',
      'height:14px',
      'border-radius:9999px',
      'border:2px solid #ffffff',
      'box-shadow:0 1px 3px rgb(15 23 42 / 0.35)',
      'background:#54821f',
    ].join(';')
    return [{ id: 'site', lngLat: [longitude, latitude], element: dot }]
  }, [latitude, longitude])

  if (!pins) return null

  return <MapCanvas pins={pins} className="h-[104px] rounded-lg ring-1 ring-ink-200" />
}

/** Street, then locality, as an envelope would have it. */
function addressLines(address: Site['address']): string[] {
  if (!address) return []
  const street = [address.unit, address.streetNumber, address.street].filter(Boolean).join(' ')
  const locality = [address.suburb ?? address.city, address.state, address.postcode]
    .filter(Boolean)
    .join(' ')
  const lines = [street, locality].filter(Boolean)
  return lines.length > 0 ? lines : address.raw ? [address.raw] : []
}

function SiteAddressForm({
  site,
  isSaving,
  error,
  onCancel,
  onSave,
}: {
  site: Site
  isSaving: boolean
  error: string | null
  onCancel: () => void
  onSave: (changes: { name: string | null; address: AddressDraft }) => void
}) {
  const [address, setAddress] = useState<AddressDraft>(() => addressDraftFrom(site.address))
  const [name, setName] = useState(site.name ?? '')

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        onSave({
          name: name.trim() || null,
          // `raw` is only rewritten when the lookup supplied one. An imported
          // address keeps the text ServiceM8 held, because that string is the
          // record of what the old system knew and correcting the parsed
          // fields is not a reason to lose it.
          address: { ...address, raw: address.raw ?? addressLine(address) },
        })
      }}
    >
      <Label>Site</Label>

      <AddressField id="site-address" value={address} onChange={setAddress} />

      <Field label="Name it (optional)" htmlFor="site-name">
        <Input
          id="site-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Warehouse 3"
        />
      </Field>

      {error ? (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-1.5">
        <p className="min-w-0 flex-1 truncate text-xs text-ink-500">
          Changes every job at this property.
        </p>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={isSaving} disabled={!addressIsUsable(address)}>
          Save
        </Button>
      </div>
    </form>
  )
}

function mapsUrl(address: NonNullable<Site['address']>): string {
  const destination =
    address.latitude !== null && address.longitude !== null
      ? `${address.latitude},${address.longitude}`
      : addressLine(address)

  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
}
